// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/feed/components/FeedVideo.module.css';
import FeedAutoplay from '@app/features/feed/state/FeedAutoplay';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getFullscreenElement} from '@app/features/platform/utils/FullscreenMediaUtils';
import {Spinner} from '@app/features/ui/components/Spinner';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {MediaFullscreenButton} from '@app/features/voice/components/media_player/components/MediaFullscreenButton';
import {MediaPipButton} from '@app/features/voice/components/media_player/components/MediaPipButton';
import {MediaPlayButton} from '@app/features/voice/components/media_player/components/MediaPlayButton';
import {MediaPlaybackRate} from '@app/features/voice/components/media_player/components/MediaPlaybackRate';
import {MediaProgressBar} from '@app/features/voice/components/media_player/components/MediaProgressBar';
import {MediaTimeDisplay} from '@app/features/voice/components/media_player/components/MediaTimeDisplay';
import {MediaVolumeControl} from '@app/features/voice/components/media_player/components/MediaVolumeControl';
import {useControlsVisibility} from '@app/features/voice/components/media_player/hooks/useControlsVisibility';
import {useMediaFullscreen} from '@app/features/voice/components/media_player/hooks/useMediaFullscreen';
import {useMediaKeyboard} from '@app/features/voice/components/media_player/hooks/useMediaKeyboard';
import {useMediaPiP} from '@app/features/voice/components/media_player/hooks/useMediaPiP';
import {useMediaPlayer} from '@app/features/voice/components/media_player/hooks/useMediaPlayer';
import {useMediaProgress} from '@app/features/voice/components/media_player/hooks/useMediaProgress';
import {mediaGainFromVolumeSetting} from '@app/features/voice/components/media_player/hooks/useMediaVolume';
import {useOffscreenRelease} from '@app/features/voice/components/media_player/hooks/useOffscreenRelease';
import {VIDEO_PLAYBACK_RATES} from '@app/features/voice/components/media_player/utils/MediaConstants';
import {clampMediaTime} from '@app/features/voice/components/media_player/utils/MediaSeekUtils';
import {
	cancelOffscreenRelease,
	scheduleOffscreenRelease,
	trackVideoPlayback,
} from '@app/features/voice/components/media_player/utils/VideoPlaybackRegistry';
import skin from '@app/features/voice/components/media_player/VideoControls.module.css';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {SpeakerSlashIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type Hls from 'hls.js';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

const logger = new Logger('FeedVideo');

const VIDEO_DESCRIPTOR = msg({
	message: 'Video',
	comment: 'Accessible name of the video player of a feed post (its play, sound and fullscreen controls).',
});
const TURN_ON_SOUND_DESCRIPTOR = msg({
	message: 'Turn on sound',
	comment:
		'Small pill over a feed video that plays by itself without sound. Pressing it turns the sound on for this and the next feed videos.',
});
const LOADING_VIDEO_DESCRIPTOR = msg({
	message: 'Loading video',
	comment: 'Accessible label of the spinner over a feed video while it waits for data.',
});

// Controls fade after this long without the pointer moving over a playing video.
const CONTROLS_AUTOHIDE_MS = 2000;
const SEEK_STEP_SECONDS = 5;
const KEY_SHORTCUTS = 'Space K M F ArrowLeft ArrowRight';
// hls.js buffers this far ahead of a muted video that plays by itself, and its own default once the sound is on.
export const AUTOPLAY_MAX_BUFFER_SECONDS = 8;
const HLS_MAX_BUFFER_SECONDS = 30;

export interface FeedVideoPlayerProps {
	src: string;
	// hls.js where the browser has no HLS of its own; null plays src on the element (native HLS, or the MP4 copy).
	Hls: typeof Hls | null;
	poster: string;
	duration: number | undefined;
	resumeAt: number | null;
	loop: boolean;
	// The feed's turn to play by itself (FeedAutoplay): losing it pauses the video and starts the offscreen release.
	active: boolean;
	onRelease: (resumeTime: number | null) => void;
	onFailed: () => void;
	// The viewer played or paused it with a control (FeedVideo claims the turn, or remembers the pause past the poster).
	onViewerPlay: () => void;
	onViewerPause: () => void;
}

// Micup akis-v3: one <video> for every source, with the feed's own controls over it. Muted or not and the volume are
// the session's (FeedAutoplay), shared by every feed video.
export const FeedVideoPlayer = observer(function FeedVideoPlayer({
	src,
	Hls: HlsPlayer,
	poster,
	duration: initialDuration,
	resumeAt,
	loop,
	active,
	onRelease,
	onFailed,
	onViewerPlay,
	onViewerPause,
}: FeedVideoPlayerProps) {
	const {i18n} = useLingui();
	const containerRef = useRef<HTMLDivElement>(null);
	const callbacksRef = useRef({onRelease, onFailed, onViewerPlay, onViewerPause});
	callbacksRef.current = {onRelease, onFailed, onViewerPlay, onViewerPause};
	const playerRef = useRef<Hls | null>(null);
	const stopLoadRef = useRef<(() => void) | null>(null);
	const [isInteracting, setIsInteracting] = useState(false);
	// From the element's own events: useMediaPlayer's isPlaying misses a play() that came before the source's loadstart.
	const [phase, setPhase] = useState<'loading' | 'playing' | 'paused'>('loading');
	// Paused with a control: coming back into view or to the window does not start it again.
	const pausedByViewerRef = useRef(false);
	const {mediaRef, state, pause, toggle, seekRelative, setPlaybackRate} = useMediaPlayer({
		mediaKind: 'video',
		loop,
		persistPlaybackRate: false,
		onLoadedMetadata: (duration, media) => {
			if (resumeAt !== null && HlsPlayer === null) media.currentTime = clampMediaTime(resumeAt, duration);
		},
		onError: (_error, code) => {
			if (code === 'error') callbacksRef.current.onFailed();
		},
	});
	const videoRef = mediaRef as React.RefObject<HTMLVideoElement | null>;
	const {muted, volume} = FeedAutoplay;
	useLayoutEffect(() => {
		const media = mediaRef.current;
		if (!media) return;
		media.muted = muted;
		media.volume = mediaGainFromVolumeSetting(volume);
		if (playerRef.current)
			playerRef.current.config.maxBufferLength = muted ? AUTOPLAY_MAX_BUFFER_SECONDS : HLS_MAX_BUFFER_SECONDS;
	}, [mediaRef, muted, volume]);
	// A browser that refuses sound without a gesture still plays it muted.
	const startPlayback = useCallback(() => {
		const media = mediaRef.current;
		if (!media) return;
		void media.play().catch((error: unknown) => {
			if (!(error instanceof Error) || error.name !== 'NotAllowedError' || media.muted) {
				if (media.paused) setPhase('paused');
				return;
			}
			FeedAutoplay.setMuted(true);
			media.muted = true;
			void media.play().catch(() => setPhase('paused'));
		});
	}, [mediaRef]);
	useEffect(() => {
		const media = mediaRef.current;
		if (!media) return;
		if (HlsPlayer === null) {
			startPlayback();
			return;
		}
		const player = new HlsPlayer({
			enableWorker: true,
			lowLatencyMode: false,
			backBufferLength: 30,
			...(FeedAutoplay.muted ? {maxBufferLength: AUTOPLAY_MAX_BUFFER_SECONDS} : {}),
			...(resumeAt !== null ? {startPosition: resumeAt} : {}),
		});
		player.on(HlsPlayer.Events.ERROR, (_event, data) => {
			if (!data.fatal) return;
			logger.warn('Feed video playback failed:', data.type, data.details);
			callbacksRef.current.onFailed();
		});
		player.loadSource(src);
		player.attachMedia(media);
		// Stopped when it loses the turn, so a paused video does not keep buffering; playing again resumes it.
		let loadStopped = false;
		const resumeLoad = () => {
			if (!loadStopped) return;
			loadStopped = false;
			player.startLoad(media.currentTime);
		};
		stopLoadRef.current = () => {
			if (loadStopped) return;
			loadStopped = true;
			player.stopLoad();
		};
		media.addEventListener('play', resumeLoad);
		playerRef.current = player;
		startPlayback();
		return () => {
			media.removeEventListener('play', resumeLoad);
			playerRef.current = null;
			stopLoadRef.current = null;
			player.destroy();
		};
	}, [HlsPlayer, mediaRef, resumeAt, src, startPlayback]);
	const pauseForOthers = useCallback(() => pause(), [pause]);
	useLayoutEffect(() => {
		const media = mediaRef.current;
		if (!media) return;
		return trackVideoPlayback(media, pauseForOthers);
	}, [mediaRef, pauseForOthers]);
	// Back to the poster: playing again goes through FeedVideo's start (fresh URLs), from where it stopped.
	const release = useCallback(() => {
		const media = mediaRef.current;
		callbacksRef.current.onRelease(media && !media.ended ? media.currentTime : null);
	}, [mediaRef]);
	useOffscreenRelease({containerRef, mediaRef, enabled: true, pause: pauseForOthers, release});
	const wasActiveRef = useRef(active);
	useEffect(() => {
		if (wasActiveRef.current === active) return;
		wasActiveRef.current = active;
		const media = mediaRef.current;
		if (!media) return;
		if (active) {
			cancelOffscreenRelease(media);
			if (!pausedByViewerRef.current) startPlayback();
			return;
		}
		if (getFullscreenElement()?.contains(media) === true || document.pictureInPictureElement === media) return;
		if (!media.paused) pause();
		stopLoadRef.current?.();
		scheduleOffscreenRelease(media, () => {
			if (media.paused) release();
		});
	}, [active, mediaRef, pause, release, startPlayback]);
	const viewerToggle = useCallback(() => {
		containerRef.current?.focus({preventScroll: true});
		const media = mediaRef.current;
		const pausing = media !== null && !media.paused;
		pausedByViewerRef.current = pausing;
		if (pausing) callbacksRef.current.onViewerPause();
		else callbacksRef.current.onViewerPlay();
		void toggle();
	}, [mediaRef, toggle]);
	// Turning the sound on means the viewer is watching: it claims the turn like a play would (muting again keeps it).
	const toggleMute = useCallback(() => {
		if (!FeedAutoplay.muted) {
			FeedAutoplay.setMuted(true);
			return;
		}
		FeedAutoplay.setVolume(FeedAutoplay.volume || 1);
		const media = mediaRef.current;
		if (media && !media.paused) callbacksRef.current.onViewerPlay();
	}, [mediaRef]);
	const {currentTime, duration, progress, buffered, seekToPercentage, startSeeking, endSeeking} = useMediaProgress({
		mediaRef,
		initialDuration,
	});
	const {isFullscreen, supportsFullscreen, toggleFullscreen} = useMediaFullscreen({containerRef, videoRef});
	const {isPiP, supportsPiP, togglePiP} = useMediaPiP({videoRef});
	const {controlsVisible, containerProps} = useControlsVisibility({
		autohideDelay: CONTROLS_AUTOHIDE_MS,
		isPlaying: phase === 'playing',
		isInteracting,
	});
	useMediaKeyboard({
		containerRef,
		captureDocumentKeys: isFullscreen,
		seekAmount: SEEK_STEP_SECONDS,
		onTogglePlay: viewerToggle,
		onSeekBackward: (amount) => seekRelative(-amount),
		onSeekForward: (amount) => seekRelative(amount),
		onVolumeUp: (step) => FeedAutoplay.setVolume(FeedAutoplay.volume + step),
		onVolumeDown: (step) => FeedAutoplay.setVolume(FeedAutoplay.volume - step),
		onToggleMute: toggleMute,
		onToggleFullscreen: toggleFullscreen,
		onSeekPercentage: seekToPercentage,
	});
	const waiting = phase === 'loading' && state.error === null;
	return (
		<FocusRing offset={-2} data-flx="feed.feed-video.player-focus-ring">
			<div
				ref={containerRef}
				className={clsx(
					styles.player,
					styles.controlsRoot,
					!controlsVisible && styles.controlsHidden,
					!controlsVisible && skin.hidden,
				)}
				role="group"
				aria-label={i18n._(VIDEO_DESCRIPTOR)}
				aria-keyshortcuts={KEY_SHORTCUTS}
				tabIndex={-1}
				data-media-fullscreen-root="true"
				data-flx="feed.feed-video.player"
				{...containerProps}
			>
				<video
					ref={videoRef}
					className={styles.media}
					src={HlsPlayer === null ? src : undefined}
					poster={poster}
					preload="auto"
					playsInline
					data-embed-media="true"
					onClick={viewerToggle}
					onPlaying={() => setPhase('playing')}
					onWaiting={() => setPhase('loading')}
					onPause={() => setPhase('paused')}
					data-flx="feed.feed-video.media"
				>
					<track kind="captions" data-flx="feed.feed-video.track" />
				</video>
				{waiting && (
					<span
						className={styles.spinner}
						role="status"
						aria-label={i18n._(LOADING_VIDEO_DESCRIPTOR)}
						data-flx="feed.feed-video.spinner"
					>
						<Spinner size="large" data-flx="feed.feed-video.spinner-icon" />
					</span>
				)}
				{muted && (
					<button type="button" className={styles.unmute} onClick={toggleMute} data-flx="feed.feed-video.unmute">
						<SpeakerSlashIcon weight="fill" className={styles.unmuteIcon} data-flx="feed.feed-video.unmute-icon" />
						{i18n._(TURN_ON_SOUND_DESCRIPTOR)}
					</button>
				)}
				<div className={clsx(styles.controls, skin.bar)} data-flx="feed.feed-video.controls">
					<div className={skin.row} data-flx="feed.feed-video.controls-row">
						<MediaPlayButton
							isPlaying={phase !== 'paused'}
							onToggle={viewerToggle}
							size="small"
							iconSize={18}
							data-flx="feed.feed-video.play-button"
						/>
						<MediaVolumeControl
							volume={volume}
							isMuted={muted}
							onVolumeChange={FeedAutoplay.setVolume}
							onToggleMute={toggleMute}
							expandable
							iconSize={18}
							data-flx="feed.feed-video.volume"
						/>
						<MediaTimeDisplay
							currentTime={currentTime}
							duration={duration}
							size="small"
							className={skin.time}
							data-flx="feed.feed-video.time"
						/>
						<span className={skin.spacer} data-flx="feed.feed-video.controls-spacer" />
						<MediaPlaybackRate
							rate={state.playbackRate}
							onRateChange={setPlaybackRate}
							rates={VIDEO_PLAYBACK_RATES}
							size="small"
							className={skin.rate}
							data-flx="feed.feed-video.rate"
						/>
						{supportsPiP && (
							<MediaPipButton
								isPiP={isPiP}
								supportsPiP={supportsPiP}
								onToggle={togglePiP}
								iconSize={18}
								size="small"
								className={skin.pip}
								data-flx="feed.feed-video.pip"
							/>
						)}
						{supportsFullscreen && (
							<MediaFullscreenButton
								isFullscreen={isFullscreen}
								supportsFullscreen={supportsFullscreen}
								onToggle={toggleFullscreen}
								iconSize={18}
								size="small"
								data-flx="feed.feed-video.fullscreen"
							/>
						)}
					</div>
					<MediaProgressBar
						progress={progress}
						buffered={buffered}
						currentTime={currentTime}
						duration={duration}
						// Frame-by-frame playhead only while it is seen moving; otherwise the bar follows timeupdate.
						mediaRef={controlsVisible ? mediaRef : undefined}
						isPlaying={phase === 'playing'}
						onSeek={seekToPercentage}
						onSeekStart={() => {
							setIsInteracting(true);
							startSeeking();
						}}
						onSeekEnd={() => {
							setIsInteracting(false);
							endSeeking();
						}}
						className={clsx(styles.progress, skin.progress)}
						compact
						data-flx="feed.feed-video.progress"
					/>
				</div>
			</div>
		</FocusRing>
	);
});
